import Banner from "@/components/Banner";
import MetricGrid from "@/components/MetricGrid";
import Tag from "@/components/Tag";
import type { JobApplicantCountEstimate } from "./queries";
import {
  EXECUSEARCH_COPY,
  FACILITY_WEBSITE_DESCRIPTION,
  FACILITY_WEBSITE_SUBTEXT,
  formatApplicantCountRange,
  formatSampleSizeCopy,
  INCOMPLETE_CRITERIA_CAPTION,
  INCOMPLETE_CRITERIA_TITLE,
  isFacilityWebsiteApplyVia,
  isLowConfidenceEstimate,
  LOW_CONFIDENCE_COPY,
  MATCH_TIER_LABELS,
  matchTierTagType,
  RANGE_LABEL,
  SELF_SERVE_IN_REVIEW_COACHING,
  shouldShowCoachingCopy,
  shouldShowExecuSearchCopy
} from "./copy";

export type JobApplicantCountEstimateBlockProps = {
  estimate?: JobApplicantCountEstimate | null;
  /** Client gate. When true, do not flash incomplete copy while a range is in flight. */
  criteriaComplete?: boolean;
  serviceLevel?: string | null;
  stage?: string | null;
  applyVia?: string | null;
};

export default function JobApplicantCountEstimateBlock({
  estimate,
  criteriaComplete,
  serviceLevel,
  stage,
  applyVia
}: JobApplicantCountEstimateBlockProps) {
  if (estimate && !estimate.criteriaComplete) {
    return (
      <div className="flex flex-col gap-6" data-testid="job-applicant-count-estimate">
        <Banner
          variant="info"
          inline
          description={INCOMPLETE_CRITERIA_TITLE}
          subText={INCOMPLETE_CRITERIA_CAPTION}
          testId="job-applicant-count-estimate-incomplete"
        />
      </div>
    );
  }

  if (!estimate) {
    if (criteriaComplete) {
      return (
        <div className="flex flex-col gap-6" data-testid="job-applicant-count-estimate">
          <MetricGrid
            hasHeading
            size="compact"
            layout="stacked"
            metrics={[
              {
                iconName: "group",
                label: RANGE_LABEL,
                loading: true,
                testId: "job-applicant-count-estimate-range"
              }
            ]}
          />
        </div>
      );
    }

    return (
      <div className="flex flex-col gap-6" data-testid="job-applicant-count-estimate">
        <Banner
          variant="info"
          inline
          description={INCOMPLETE_CRITERIA_TITLE}
          subText={INCOMPLETE_CRITERIA_CAPTION}
          testId="job-applicant-count-estimate-incomplete"
        />
      </div>
    );
  }

  const range = formatApplicantCountRange(estimate.low, estimate.high);
  const sampleCopy = formatSampleSizeCopy(estimate.sampleSize);
  const matchTierLabel = MATCH_TIER_LABELS[estimate.matchTier];
  const lowConfidence = isLowConfidenceEstimate(estimate);
  const coaching = shouldShowCoachingCopy(estimate, serviceLevel, stage);
  const execuSearchCopy = shouldShowExecuSearchCopy(serviceLevel);
  const facilityWebsite = isFacilityWebsiteApplyVia(applyVia);

  return (
    <div className="flex flex-col gap-6" data-testid="job-applicant-count-estimate">
      <MetricGrid
        hasHeading
        size="compact"
        layout="stacked"
        metrics={[
          {
            iconName: "group",
            label: RANGE_LABEL,
            value: range,
            testId: "job-applicant-count-estimate-range"
          }
        ]}
      />
      <div className="flex flex-col gap-2">
        <p className="text-body-medium text-sys-brand-on-surface" data-testid="job-applicant-count-estimate-sample">
          {sampleCopy}
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-body-medium text-sys-brand-on-surface-variant">Match</span>
          <Tag
            size="small"
            compact
            tagType={matchTierTagType(estimate.matchTier)}
            tagStyle="standard"
            testId="job-applicant-count-estimate-match-tier"
          >
            {matchTierLabel}
          </Tag>
        </div>
        <p
          className="text-body-medium text-sys-brand-on-surface-variant"
          data-testid="job-applicant-count-estimate-disclaimer"
        >
          {estimate.disclaimer}
        </p>
        {execuSearchCopy && !coaching ? (
          <p
            className="text-body-medium text-sys-brand-on-surface"
            data-testid="job-applicant-count-estimate-execusearch"
          >
            {EXECUSEARCH_COPY}
          </p>
        ) : null}
      </div>
      {facilityWebsite ? (
        <Banner
          variant="info"
          inline
          description={FACILITY_WEBSITE_DESCRIPTION}
          subText={FACILITY_WEBSITE_SUBTEXT}
          testId="job-applicant-count-estimate-facility-website"
        />
      ) : null}
      {coaching ? (
        <Banner
          variant={lowConfidence ? "warn" : "info"}
          inline
          description={SELF_SERVE_IN_REVIEW_COACHING}
          subText={lowConfidence ? LOW_CONFIDENCE_COPY : undefined}
          testId="job-applicant-count-estimate-coaching"
        />
      ) : lowConfidence ? (
        <Banner
          variant="warn"
          inline
          description={LOW_CONFIDENCE_COPY}
          testId="job-applicant-count-estimate-low-confidence"
        />
      ) : null}
    </div>
  );
}
